/**
 * `org` procedure group. Org == tenant; there is no second concept.
 *
 * `organizations` / `members` are better-auth owned; `org_settings` is our sidecar.
 */

import { z } from 'zod';
import {
  isoDateTimeSchema,
  jsonObjectSchema,
  nonBlankString,
  timezoneSchema,
  uuidSchema,
} from './common.ts';
import { orgRoleSchema, recordingConsentModeSchema } from './enums.ts';

/**
 * `[a-z0-9]` with internal hyphens; the tenant's URL segment. Applied to *input* only
 * — responses echo whatever is already stored, so a legacy slug can never make a
 * perfectly good org fail response validation.
 */
export const orgSlugSchema = z
  .string()
  .min(2)
  .max(63)
  .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'expected a lowercase slug');

export const organizationSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  slug: z.string(),
  logo: z.url().nullable(),
  metadata: jsonObjectSchema.nullable(),
  createdAt: isoDateTimeSchema,
});

/** A row of `org.list`: the org plus the caller's standing in it. */
export const orgMembershipSchema = organizationSchema.extend({
  role: orgRoleSchema,
  memberCount: z.int().min(0),
  isActive: z.boolean(),
  joinedAt: isoDateTimeSchema,
});

export const orgSettingsSchema = z.object({
  orgId: uuidSchema,
  timezone: timezoneSchema,
  enrichmentMonthlyCreditCap: z.int().min(0),
  enrichmentAutoOnCreate: z.boolean(),
  /**
   * Whether vendor-enriched fields may cross a share grant. Legal default is OFF;
   * see the enrichment audit — payloads are licensed per tenant.
   */
  allowEnrichedFieldSharing: z.boolean(),
  llmMonthlyTokenCapTop: z.int().min(0),
  recordingDefaultConsentMode: recordingConsentModeSchema,
  /**
   * The org's assertion that its meetings fall under ALL-PARTY consent.
   *
   * Jurisdiction is underivable from a calendar event — attendee domains say
   * nothing about where anyone sits, and one timezone spans states with opposite
   * rules — so `disclose_two_party_only` needs this input to be decidable at
   * all. Defaults TRUE: a workspace that never touches it discloses on every
   * recording, which is the direction that is merely annoying when wrong.
   */
  recordingAllPartyConsentAssumed: z.boolean(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});

// --- inputs ------------------------------------------------------------------

export const orgListInputSchema = z.object({
  includeInactive: z.boolean().default(false),
});

export const orgGetInputSchema = z.object({
  orgId: uuidSchema,
});

export const orgCreateInputSchema = z.object({
  name: nonBlankString(200, 'name'),
  /** Omitted means "derive from name"; collisions are resolved server-side. */
  slug: orgSlugSchema.optional(),
  timezone: timezoneSchema.optional(),
});

export const orgSettingsGetInputSchema = z.object({
  orgId: uuidSchema,
});

/**
 * PATCH semantics: absent keys are untouched. No "at least one key" refinement — a
 * no-op patch is harmless, and a top-level refinement would break oRPC's path-param
 * extraction for `/orgs/{orgId}/settings`.
 */
export const orgSettingsUpdateInputSchema = z.object({
  orgId: uuidSchema,
  timezone: timezoneSchema.optional(),
  enrichmentMonthlyCreditCap: z.int().min(0).max(1_000_000).optional(),
  enrichmentAutoOnCreate: z.boolean().optional(),
  allowEnrichedFieldSharing: z.boolean().optional(),
  llmMonthlyTokenCapTop: z.int().min(0).optional(),
  recordingDefaultConsentMode: recordingConsentModeSchema.optional(),
  recordingAllPartyConsentAssumed: z.boolean().optional(),
});

export const orgSwitchActiveInputSchema = z.object({
  orgId: uuidSchema,
});

export const orgSwitchActiveOutputSchema = z.object({
  orgId: uuidSchema,
  role: orgRoleSchema,
  switchedAt: isoDateTimeSchema,
});

export const orgListOutputSchema = z.object({
  items: z.array(orgMembershipSchema),
  activeOrgId: uuidSchema.nullable(),
});

// --- inferred types ----------------------------------------------------------

export type Organization = z.output<typeof organizationSchema>;
export type OrgMembership = z.output<typeof orgMembershipSchema>;
export type OrgSettings = z.output<typeof orgSettingsSchema>;
export type OrgCreateInput = z.input<typeof orgCreateInputSchema>;
export type OrgSettingsUpdateInput = z.input<typeof orgSettingsUpdateInputSchema>;

// --- invitations (Wave-4 lane S) ---------------------------------------------

/**
 * `org.invitations.*` — the OWNER-PROVISIONED path that replaces self-serve
 * signup (docs/PLAN.md: "No self-serve signup — owner provisions").
 *
 * Redemption is deliberately NOT here. It is a raw route,
 * `POST /invitations/redeem`, because it is unauthenticated by construction —
 * the whole point is that the redeemer has no account yet — and every procedure
 * in this contract runs behind the `authed` middleware. Same reasoning, and the
 * same shape, as `POST /devices/pair`.
 */
export const orgInvitationSchema = z.object({
  id: uuidSchema,
  orgId: uuidSchema,
  /** Normalized server-side; the response echoes what was stored. */
  email: z.string(),
  role: orgRoleSchema,
  invitedByUserId: uuidSchema.nullable(),
  expiresAt: isoDateTimeSchema,
  acceptedAt: isoDateTimeSchema.nullable(),
  acceptedByUserId: uuidSchema.nullable(),
  revokedAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
  /** Derived server-side so four clients do not each re-derive the same state. */
  status: z.enum(['pending', 'accepted', 'revoked', 'expired']),
});

export const orgInvitationCreateInputSchema = z.object({
  orgId: uuidSchema,
  email: z.email(),
  role: orgRoleSchema.default('member'),
  /** Default one week: long enough for a person on holiday, short enough to matter. */
  expiresInHours: z.int().min(1).max(720).default(168),
});

/**
 * The response carries the plaintext token EXACTLY ONCE — only its sha256 is
 * stored, exactly like the device-pairing key and the share invitation. There is
 * no procedure that can show it again; a lost link is re-invited, not recovered.
 */
export const orgInvitationCreateOutputSchema = z.object({
  invitation: orgInvitationSchema,
  token: z.string(),
});

export const orgInvitationListInputSchema = z.object({
  orgId: uuidSchema,
  /** Accepted, revoked and expired rows are excluded unless this is set. */
  includeInactive: z.boolean().default(false),
  limit: z.int().min(1).max(200).default(100),
});

export const orgInvitationListOutputSchema = z.object({
  items: z.array(orgInvitationSchema),
});

export const orgInvitationRevokeInputSchema = z.object({
  orgId: uuidSchema,
  invitationId: uuidSchema,
});

export type OrgInvitation = z.output<typeof orgInvitationSchema>;
export type OrgInvitationCreateInput = z.input<typeof orgInvitationCreateInputSchema>;
